import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthSession } from '../../auth/AuthBootstrap.js';
import { parseDevToken } from '../../auth/devToken.js';
import { resolveApiOrigin } from '../../api/api-origin.js';

/**
 * Getting started — the in-product walkthrough.
 *
 * Five flows, increasing in depth. Each ends with a concrete artifact
 * the user can point at and say "I made that work."
 *
 * Where possible, code blocks are pre-filled with the signed-in user's
 * actual tenant + user UUIDs (pulled from the auth context). API keys
 * are NEVER embedded — the page directs users to mint their own at
 * /admin/api-keys. That keeps the page safe to share screenshots from
 * and avoids a credential leaking via copy-paste.
 *
 * The repo has a markdown twin at docs/getting-started-as-an-operator.md
 * for users who never log into the SPA. The two are kept in rough sync
 * by hand; updating one without the other is fine — they serve
 * different audiences.
 */
export function HelpPage(): JSX.Element {
  const session = useAuthSession();
  const parsed = parseDevToken(session.token);
  const tenantId = parsed?.tenantId ?? '<your-tenant-id>';
  const apiBase = inferApiBase();

  return (
    <div className="stack-md" style={{ maxWidth: 'var(--max-content-width, 920px)' }}>
      <header className="stack-sm">
        <h1 style={{ fontSize: 'var(--font-size-2xl)', margin: 0 }}>
          Getting started with Canopy Sage
        </h1>
        <p className="text-mute">
          Five hands-on flows for someone who hasn't <em>used</em> Sage yet, just heard about it.
          Each ends with a concrete artifact you can point at. About 45 minutes end-to-end if you
          want to try them all.
        </p>
      </header>

      <Section n={0} title="Setup once" summary="Mint your own API key and stash two shell vars.">
        <p>
          Most flows below are <code>curl</code> against the Sage API. Mint a 7-day key for yourself
          rather than reusing someone else's:
        </p>
        <ol className="stack-sm">
          <li>
            Open <Link to="/admin/api-keys">Admin → API Keys</Link>
          </li>
          <li>
            Click <strong>Mint new key</strong>. Label it{' '}
            <code>{`${parsed?.userShort ?? 'me'}-laptop`}</code>, expiry 7 days.
          </li>
          <li>
            <strong>Copy the plaintext immediately</strong> — Sage stores only the SHA-256 hash and
            shows the full string once.
          </li>
        </ol>
        <p>Then in your shell:</p>
        <CodeBlock
          language="bash"
          code={`export SAGE_KEY='cnr_…paste-your-key-here'
export SAGE_API='${apiBase}'`}
        />
      </Section>

      <Section
        n={1}
        title="See what's there"
        summary="Get oriented. What datasets, templates, dashboards exist."
      >
        <p>List the major artifacts in your tenant:</p>
        <CodeBlock
          language="bash"
          code={`# Folders (top-level navigation)
curl -s -H "X-API-Key: $SAGE_KEY" $SAGE_API/folders | jq

# Data sources registered in your tenant
curl -s -H "X-API-Key: $SAGE_KEY" $SAGE_API/data-sources | jq

# Datasets (the catalog Copilot + Claude Code see)
curl -s -H "X-API-Key: $SAGE_KEY" $SAGE_API/datasets \\
  | jq '.[] | {name, mode, columns: (.columns | length)}'

# Report templates (designs)
curl -s -H "X-API-Key: $SAGE_KEY" $SAGE_API/report-templates \\
  | jq '.[] | {id, name, audience}'

# Dashboards (designs)
curl -s -H "X-API-Key: $SAGE_KEY" $SAGE_API/dashboards | jq

# Schedules
curl -s -H "X-API-Key: $SAGE_KEY" $SAGE_API/schedules | jq`}
        />
        <p className="text-mute text-sm">
          The same surfaces are at <Link to="/templates">Templates</Link>,{' '}
          <Link to="/insights">Insights</Link>,{' '}
          <Link to="/admin/data-sources">Admin → Data Sources</Link> in this UI — pick whichever
          feels faster.
        </p>
      </Section>

      <Section
        n={2}
        title="Render an existing report as PDF"
        summary="Take a published template, run it against real data, end up with a PDF on your laptop."
      >
        <CodeBlock
          language="bash"
          code={`# 1. Pick a template id from the list
TPL=<paste-a-template-id>

# 2. Render to PDF — params depend on the template's contract
curl -s -X POST \\
  -H "X-API-Key: $SAGE_KEY" \\
  -H 'content-type: application/json' \\
  -D /tmp/headers.txt \\
  -o /tmp/report.pdf \\
  -d '{
    "format": "pdf",
    "params": {
      "Canopy_ClientID": "${tenantId}"
    }
  }' \\
  "$SAGE_API/report-templates/$TPL/render"

# 3. Inspect what came back
grep -i '^x-canopy-' /tmp/headers.txt   # run-id, row count, duration
ls -la /tmp/report.pdf
open /tmp/report.pdf                    # macOS opens in Preview`}
        />
        <Callout>
          <strong>What to notice.</strong> Every render gets a stable <code>x-canopy-run-id</code>{' '}
          you can later re-fetch with <code>GET /report-runs/&lt;runId&gt;/download</code> or
          deliver via <code>POST /report-runs/&lt;runId&gt;/deliver</code>. The duration header
          tells you how long Sage took end-to-end (typically 100&ndash;200&nbsp;ms when the dataset
          is warm).
        </Callout>
      </Section>

      <Section
        n={3}
        title="Mint and revoke a personal API key"
        summary="Understand the auth model end-to-end."
      >
        <CodeBlock
          language="bash"
          code={`# 1. Mint a 7-day key
EXPIRES=$(date -u -v+7d +'%Y-%m-%dT%H:%M:%SZ')
RESPONSE=$(curl -s -X POST \\
  -H "X-API-Key: $SAGE_KEY" \\
  -H 'content-type: application/json' \\
  -d "{\\"label\\":\\"my-laptop\\",\\"expiresAt\\":\\"$EXPIRES\\"}" \\
  $SAGE_API/api-keys)
echo "$RESPONSE" | jq

NEW_KEY=$(echo "$RESPONSE" | jq -r .plaintext)
NEW_ID=$(echo "$RESPONSE" | jq -r .id)

# 2. Use it
curl -s -H "X-API-Key: $NEW_KEY" $SAGE_API/datasets | jq '.[].name'

# 3. List keys (shows lastUsedAt + usageCount)
curl -s -H "X-API-Key: $SAGE_KEY" $SAGE_API/api-keys | jq

# 4. Revoke
curl -s -o /dev/null -w 'HTTP %{http_code}\\n' -X DELETE \\
  -H "X-API-Key: $SAGE_KEY" \\
  $SAGE_API/api-keys/$NEW_ID
# → 204

# 5. Confirm revoked
curl -s -o /dev/null -w 'after revoke: HTTP %{http_code}\\n' \\
  -H "X-API-Key: $NEW_KEY" \\
  $SAGE_API/datasets
# → 401`}
        />
        <Callout>
          <strong>What to notice.</strong> The plaintext key is shown ONCE on mint &mdash; Sage
          stores only the SHA-256 hash and a 6-char hint. Lose the plaintext and the only fix is to
          mint a new key + revoke the old. Roles are inherited from the owning user at request time;
          if you remove the owner's <code>ReportAdmin</code> role, every key they minted instantly
          loses it.
        </Callout>
      </Section>

      <Section
        n={4}
        title="Author a tiny template via Claude Code + Sage MCP"
        summary="Feel the AI-driven authoring loop. The Claude Code path is the canonical one — the SPA's AI Designer is a UX shell over the same MCP tools."
      >
        <p>
          One-time setup — <Link to="/admin/claude-code">Admin → Claude Code</Link> has the install
          walkthrough specific to your environment. Once registered, your Claude Code workspace gets
          a <code>canopy-sage</code> MCP server with ~25 tools.
        </p>
        <p>The session — paste these prompts in order:</p>
        <ol className="stack-sm">
          <li>
            <em>
              "Call <code>get_authoring_standards</code> and summarize what's available."
            </em>
            <br />
            Loads design tokens, helper catalog, widget types, layout kinds. Becomes Claude's
            working knowledge for the session.
          </li>
          <li>
            <em>
              "Call <code>list_datasets</code> and show me what's available."
            </em>
          </li>
          <li>
            <em>
              "I want to author a one-page report that shows the title + period from{' '}
              <code>hub.newsletter_data</code> for product <code>monitoring</code>, last week. Use
              the design tokens. Validate before showing me the template."
            </em>
            <br />
            Claude drafts → calls <code>validate_report_template</code>, iterates on errors → calls{' '}
            <code>preview_report_template</code> against real data → shows you the result.
          </li>
          <li>
            <em>"Publish it as 'My test — title and period'."</em>
            <br />
            Claude calls <code>create_report_template</code>. Refresh{' '}
            <Link to="/templates">Templates</Link> to see the new entry.
          </li>
        </ol>
        <Callout>
          <strong>What to notice.</strong> The CSS linter rejects raw colors / font sizes — Claude
          has to use <code>var(--color-text-primary)</code> etc. That's why every Sage report looks
          consistent. Validation is continuous; Claude doesn't write 200 lines and find out at
          publish time. <code>preview_report_template</code> runs the actual render path against
          actual data, so what you see is what production produces. Publishing is the LAST step.
        </Callout>
      </Section>

      <Section
        n={5}
        title="Create + share a kiosk URL for a dashboard"
        summary="Understand the embed pattern: short-lived token, no login required, drill-through via postMessage."
      >
        <CodeBlock
          language="bash"
          code={`# 1. List dashboards in your tenant
curl -s -H "X-API-Key: $SAGE_KEY" $SAGE_API/dashboards \\
  | jq '.[] | {id, name}'

DASH=<paste-a-dashboard-id>

# 2. Mint a kiosk token good for 1 hour
RESPONSE=$(curl -s -X POST \\
  -H "X-API-Key: $SAGE_KEY" \\
  -H 'content-type: application/json' \\
  -d '{"expiresInHours": 1, "label": "tour-demo"}' \\
  $SAGE_API/dashboards/$DASH/kiosk-tokens)

echo "$RESPONSE" | jq
URL=$(echo "$RESPONSE" | jq -r .url)
echo "open this in a fresh browser window: $URL"`}
        />
        <Callout>
          <strong>What to notice.</strong> The URL contains a <code>cdk_…</code> token. Anyone with
          the URL sees the dashboard at render-time &mdash; no login required, that's the point.
          Token has an <code>expiresAt</code>; after that, the URL 404s. The kiosk page sets{' '}
          <code>X-Robots-Tag: noindex</code>. Click any element with a drill-target &mdash; it{' '}
          <code>postMessage</code>s <code>{`{ type: 'canopy.drill', href }`}</code> to the parent,
          which is how host apps route drills into their own router.
        </Callout>
      </Section>

      <Section n={6} title="What to read next" summary="Cross-references for going deeper.">
        <ul className="stack-sm">
          <li>
            <strong>
              <Link to="/admin/standards">Admin → Standards</Link>
            </strong>{' '}
            — the live design-token catalog, helper list, widget types. The constraint surface every
            author works against.
          </li>
          <li>
            <strong>
              <Link to="/admin/claude-code">Admin → Claude Code</Link>
            </strong>{' '}
            — environment-specific MCP install walkthrough.
          </li>
          <li>
            <strong>
              <Link to="/admin/audit">Admin → Audit</Link>
            </strong>{' '}
            — every artifact change, every render, every delivery, every tenant-boundary refusal.
          </li>
          <li>
            <strong>
              <code>INTEGRATION.md</code>
            </strong>{' '}
            in the repo — the canonical contract for any host app integrating with Sage.
          </li>
          <li>
            <strong>
              <code>infra/authoring-kit/CLAUDE.md</code>
            </strong>{' '}
            — what Claude Code reads automatically. Useful for humans too — it's the authoring rules
            in plain English.
          </li>
          <li>
            <strong>
              <code>docs/adr/</code>
            </strong>{' '}
            — three ADRs explaining the big architectural choices: semantic-layer, product
            positioning, deployment topology.
          </li>
        </ul>
      </Section>
    </div>
  );
}

// ─── Section wrapper ────────────────────────────────────────────────

interface SectionProps {
  n: number;
  title: string;
  summary?: string;
  children: React.ReactNode;
}

function Section({ n, title, summary, children }: SectionProps): JSX.Element {
  const id = `flow-${n}`;
  return (
    <section
      id={id}
      className="card stack-sm"
      style={{ padding: 'var(--space-5)', scrollMarginTop: 'var(--space-4)' }}
    >
      <header className="stack-2xs">
        <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'baseline' }}>
          <span
            className="badge badge--neutral"
            style={{
              fontVariantNumeric: 'tabular-nums',
              minWidth: '2ch',
              textAlign: 'center',
            }}
          >
            {n === 0 ? '0' : n}
          </span>
          <h2 style={{ fontSize: 'var(--font-size-lg)', margin: 0 }}>{title}</h2>
        </div>
        {summary && (
          <p className="text-mute text-sm" style={{ margin: 0 }}>
            {summary}
          </p>
        )}
      </header>
      <div className="stack-sm">{children}</div>
    </section>
  );
}

// ─── Code block with copy button ────────────────────────────────────

function CodeBlock({ code, language }: { code: string; language?: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard might be blocked in iframe contexts; ignore */
    }
  }
  return (
    <div
      style={{
        position: 'relative',
        background: 'var(--color-surface-3)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => void copy()}
        style={{
          position: 'absolute',
          top: 'var(--space-2)',
          right: 'var(--space-2)',
          fontSize: 'var(--font-size-xs)',
          padding: 'var(--space-1) var(--space-2)',
          background: 'var(--color-surface-2)',
          color: copied
            ? 'var(--color-success-fg, var(--color-success))'
            : 'var(--color-text-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          fontFamily: 'var(--font-mono)',
        }}
        aria-label={copied ? 'Copied' : 'Copy code'}
      >
        {copied ? '✓ copied' : 'copy'}
      </button>
      <pre
        style={{
          margin: 0,
          padding: 'var(--space-4)',
          paddingRight: 'var(--space-7)',
          overflow: 'auto',
          fontSize: 'var(--font-size-sm)',
          fontFamily: 'var(--font-mono)',
          lineHeight: 'var(--line-height-snug)',
          color: 'var(--color-text-primary)',
        }}
      >
        <code data-language={language}>{code}</code>
      </pre>
    </div>
  );
}

function Callout({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        background: 'var(--color-accent-softer)',
        borderLeft: '3px solid var(--color-accent-border)',
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-text-secondary)',
      }}
    >
      {children}
    </div>
  );
}

// ─── API base URL inference ─────────────────────────────────────────

function inferApiBase(): string {
  // Same resolver as src/api/client.ts — derives the env's API host
  // (api.<host>) on the production Sage hostnames, else VITE_API_BASE_URL.
  return `${resolveApiOrigin()}/reporting/api`;
}
